import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query/query-keys';
import { apiFetch } from '@/lib/api/client';

interface ApiErrorPayload {
  error?: {
    message?: string;
  };
}

function extractErrorMessage(payload: unknown, fallback: string): string {
  if (payload && typeof payload === 'object' && 'error' in payload) {
    const parsed = payload as ApiErrorPayload;
    const message = parsed.error?.message;
    if (typeof message === 'string' && message.trim().length > 0) {
      return message;
    }
  }

  return fallback;
}

interface CityRecord {
  id?: unknown;
  name?: unknown;
}

function isCityRecord(value: unknown): value is Required<CityRecord> {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const record = value as CityRecord;
  return (
    (typeof record.id === 'number' || typeof record.id === 'string') &&
    typeof record.name === 'string'
  );
}

export interface CityOption {
  id: string;
  code: string;
  name: string;
  name_en: string;
}

async function fetchCities(countryId: string): Promise<CityOption[]> {
  const response = await apiFetch(`/api/v1/countries/${countryId}/cities`);
  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    const message = extractErrorMessage(
      payload,
      `Failed to load cities: ${response.status} ${response.statusText}`
    );
    throw new Error(message);
  }

  if (!Array.isArray(payload)) {
    return [];
  }

  return payload.filter(isCityRecord).map((item) => {
    const id = String(item.id);
    const name = item.name as string;
    return {
      id,
      code: id,
      name,
      name_en: name,
    };
  });
}

export function useCities(countryId?: string) {
  return useQuery({
    queryKey: queryKeys.cities(countryId ?? ''),
    queryFn: () => fetchCities(countryId!),
    enabled: !!countryId,
    staleTime: 1000 * 60 * 30, // Cache for 30 minutes
  });
}
